import { DiscoveryJob } from './discovery';
export type AdoptionStatus = 'pending' | 'created' | 'linked' | 'existing' | 'needs_review';
export type AdoptionSelection = { mode: 'all' } | { mode: 'ids'; supplier_hotel_ids: number[] };
export interface AdoptionRequest {
  request_id: string;
  discovery_job_id: string;
  selection: AdoptionSelection;
  prefer_source_content: boolean;
  reason: string;
}
export interface AdoptionJob {
  job_id: string;
  discovery_job_id: string;
  status: DiscoveryJob['status'];
  attempt_count: number;
  last_error_code: string | null;
  prefer_source_content: boolean;
  created_at: string;
  updated_at: string;
  completed_at: string | null;
  counts: Record<AdoptionStatus | 'total', number>;
}
export interface AdoptionHotel {
  supplier_hotel_id: number;
  status: AdoptionStatus;
  hotel_id: string | null;
  review_code:
    | 'connection_disabled'
    | 'conflicting_identifiers'
    | 'possible_duplicate'
    | 'insufficient_identity'
    | null;
  candidate_hotel_ids: string[];
  source_revision: string | null;
  version: number;
  hotel_code: string;
  display_name: string | null;
  country_code: string | null;
  locality: string | null;
  address: string | null;
}
// Read only the source-ID column. Quoted commas/newlines in an exported hotel
// description must not turn into another selection or an executable spreadsheet value.
export function adoptionCsvIds(input: string): number[] {
  if (input.length > 5_000_000) throw new Error('size');
  const text = input.replace(/^\uFEFF/, '');
  const [first] = text.split(/\r?\n/, 1);
  const delimiter = first.includes(',') ? ',' : first.includes(';') ? ';' : '\t';
  const rows: string[][] = [];
  let row: string[] = [],
    cell = '',
    quoted = false,
    afterQuote = false;
  const endCell = () => {
    row.push(cell);
    cell = '';
    afterQuote = false;
  };
  const endRow = () => {
    endCell();
    if (row.some((c) => c.trim())) rows.push(row);
    row = [];
    if (rows.length > 100001) throw new Error('size');
  };
  for (let i = 0; i < text.length; i++) {
    const c = text[i];
    if (quoted) {
      if (c === '"') {
        if (text[i + 1] === '"') {
          cell += '"';
          i++;
        } else {
          quoted = false;
          afterQuote = true;
        }
      } else cell += c;
    } else if (c === delimiter) endCell();
    else if (c === '\n' || c === '\r') {
      if (c === '\r' && text[i + 1] === '\n') i++;
      endRow();
    } else if (c === '"' && !cell && !afterQuote) quoted = true;
    else {
      if (afterQuote || c === '"') throw new Error('csv');
      cell += c;
    }
  }
  if (quoted) throw new Error('csv');
  if (cell || row.length || afterQuote) endRow();
  if (!rows.length) throw new Error('empty');
  const header = rows[0].map((c) => c.trim());
  const index = header.indexOf('supplier_hotel_id');
  if (index >= 0 && header.filter((c) => c === 'supplier_hotel_id').length !== 1) throw new Error('header');
  const data = index >= 0 ? rows.slice(1) : rows;
  const ids = new Set<number>();
  for (const cells of data) {
    if (index < 0 && cells.length !== 1) throw new Error('header');
    const value = (cells[index < 0 ? 0 : index] || '').trim();
    if (!/^[1-9]\d*$/.test(value) || !Number.isSafeInteger(Number(value))) throw new Error('id');
    ids.add(Number(value));
  }
  if (!ids.size || ids.size > 100000) throw new Error('size');
  return [...ids].sort((a, b) => a - b);
}
